/**
 * useOverlayUrlSync
 *
 * Two-way bridge between the URL search params and the
 * OverlayOrchestrator stack:
 *
 *   1. **Hydration (cold load)** — on the first render, parse the URL
 *      via `overlaySurfaceRegistry`. If a recognised, restorable surface
 *      is present and not already on the stack, open it.
 *      Unrecognised overlay params are cleared from the URL.
 *
 *   2. **Serialization (state → URL)** — when the top of the stack
 *      changes, write/clear the overlay params on the current URL.
 *
 *   3. **External URL changes** — when the user pastes a new URL or the
 *      router otherwise mutates search params, react accordingly without
 *      thrashing against our own writes.
 *
 * Loop protection: a single `lastUrlSignatureRef` records the most
 * recent overlay-param signature we WROTE. Subsequent searchParams
 * effects compare against that signature and short-circuit if they
 * match — preventing the canonical state→URL→state ping-pong.
 *
 * Mounted once inside OverlayOrchestratorProvider.
 *
 * @module lib/navigation/useOverlayUrlSync
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as orchestrator from './overlayOrchestrator';
import {
    parseOverlayFromUrl,
    serializeOverlayToUrl,
    canRestoreFromUrl,
    OVERLAY_PARAM,
    ENTITY_ID_PARAM,
    TAB_PARAM,
    LEGACY_PANEL_PARAM,
} from './overlaySurfaceRegistry';
import type { OverlayEntry } from './types';
import { surfaceKey } from './types';

const OVERLAY_KEYS = [OVERLAY_PARAM, ENTITY_ID_PARAM, TAB_PARAM, LEGACY_PANEL_PARAM] as const;

/** Build a stable signature of the overlay-relevant URL params. */
function overlaySignature(params: URLSearchParams): string {
    const hasOverlayParams = Boolean(
        params.get(OVERLAY_PARAM) ||
        params.get(ENTITY_ID_PARAM) ||
        params.get(LEGACY_PANEL_PARAM)
    );

    return OVERLAY_KEYS.map((k) => {
        if (k === TAB_PARAM && !hasOverlayParams) return `${k}=`;
        return `${k}=${params.get(k) ?? ''}`;
    }).join('|');
}

/** Apply a patch (key→value|undefined) into a fresh URLSearchParams. */
function applyPatch(
    base: URLSearchParams,
    patch: Record<string, string | undefined>,
): URLSearchParams {
    const next = new URLSearchParams(base);
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === '') next.delete(key);
        else next.set(key, value);
    }
    return next;
}

/** Topmost visible overlay or null. */
function visibleTop(stack: readonly OverlayEntry[]): OverlayEntry | null {
    for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].lifecycle !== 'closed' && stack[i].lifecycle !== 'closing') {
            return stack[i];
        }
    }
    return null;
}

export function useOverlayUrlSync(stack: readonly OverlayEntry[]): void {
    const [searchParams, setSearchParams] = useSearchParams();

    /** Last overlay-signature we WROTE. Used to ignore our own URL writes. */
    const lastWrittenSignatureRef = useRef<string | null>(null);
    /** True until the cold-load hydration attempt has run once. */
    const hydratedRef = useRef(false);

    // ── 1) Cold-load hydration ────────────────────────────────────────────
    //
    // Runs exactly once per mount. If the URL already encodes an overlay
    // and that surface is restorable, push it onto the orchestrator stack.
    // Invalid/unknown overlay params are cleared so the URL doesn't
    // permanently advertise a broken deep-link.
    useEffect(() => {
        if (hydratedRef.current) return;
        hydratedRef.current = true;

        const desc = parseOverlayFromUrl(searchParams);

        // No overlay in URL → nothing to hydrate, record clean signature.
        if (!desc) {
            lastWrittenSignatureRef.current = overlaySignature(searchParams);
            // If overlay/entityId keys exist but didn't parse, scrub them.
            if (
                searchParams.get(OVERLAY_PARAM) ||
                searchParams.get(LEGACY_PANEL_PARAM)
            ) {
                const cleaned = applyPatch(searchParams, serializeOverlayToUrl(null));
                lastWrittenSignatureRef.current = overlaySignature(cleaned);
                setSearchParams(cleaned, { replace: true });
            }
            return;
        }

        // Surface is known but not restorable in this orchestrator — the
        // page-local host (e.g. InvestorDrawerHost) will mount it. Just
        // record the signature so we don't fight that host.
        if (!canRestoreFromUrl(desc.name)) {
            lastWrittenSignatureRef.current = overlaySignature(searchParams);
            return;
        }

        // Already open? Nothing to do.
        const key = surfaceKey(desc.surface);
        const alreadyOpen = orchestrator.getStack().some(
            (e) => surfaceKey(e.surface) === key && e.lifecycle !== 'closed',
        );
        if (alreadyOpen) {
            lastWrittenSignatureRef.current = overlaySignature(searchParams);
            return;
        }

        // Open via orchestrator with a synthetic "deep-link" origin.
        try {
            orchestrator.openOverlay({
                surface: desc.surface,
                overlayKind: 'drawer',
                origin: {
                    origin: { kind: 'page', name: 'deep-link' },
                    context: {
                        surface: { kind: 'page', name: 'deep-link' },
                        pathname: window.location.pathname,
                        search: window.location.search,
                        hash: window.location.hash,
                        capturedAt: Date.now(),
                    },
                    triggerElementId: undefined,
                },
                props: desc.tab ? { tab: desc.tab } : undefined,
            });
            lastWrittenSignatureRef.current = overlaySignature(searchParams);
        } catch (err) {
            // Surface registry hasn't registered the canonical-host or
            // migration-boundary entry → fail gracefully and clear params.
            if (process.env.NODE_ENV !== 'production') {
                console.warn(
                    '[overlay-url-sync] Failed to restore overlay from URL:',
                    desc.name,
                    err,
                );
            }
            const cleaned = applyPatch(searchParams, serializeOverlayToUrl(null));
            lastWrittenSignatureRef.current = overlaySignature(cleaned);
            setSearchParams(cleaned, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // ── 2) Serialization (orchestrator stack → URL) ──────────────────────
    //
    // When the top changes, push (or clear) overlay params. Writes use
    // `replace: true` so each open/close doesn't pollute history — the
    // sentinel pushState in OverlayOrchestratorProvider already handles
    // back-button behaviour.
    //
    // Guard: we only write to the URL when the URL already matches the
    // signature we last wrote. If the URL has drifted (external change,
    // route nav, paste), defer to effect #3 which will reconcile state
    // FROM the URL — otherwise we'd race and strip a deep-link before it
    // is parsed.
    useEffect(() => {
        if (!hydratedRef.current) return;

        // Page-owned drawers are recognized during hydration but never placed
        // on this stack. An empty stack is therefore not a request to close
        // them or erase their record IDs (including through compatibility redirects).
        const pageOwned = parseOverlayFromUrl(searchParams);
        if (pageOwned && !canRestoreFromUrl(pageOwned.name)) return;

        const currentSig = overlaySignature(searchParams);
        if (currentSig !== lastWrittenSignatureRef.current) {
            // URL changed externally — let effect #3 own the reconciliation.
            return;
        }

        const top = visibleTop(stack);
        const patch = top
            ? serializeOverlayToUrl({
                surface: top.surface,
                tab: typeof top.props?.tab === 'string' ? top.props.tab : undefined,
            })
            : serializeOverlayToUrl(null);

        const next = applyPatch(searchParams, patch);
        const nextSig = overlaySignature(next);

        if (nextSig === currentSig) {
            return; // No-op write.
        }

        lastWrittenSignatureRef.current = nextSig;
        setSearchParams(next, { replace: true });
        // We deliberately do NOT include `searchParams` in the dependency
        // array beyond the access above — this effect only fires on stack
        // transitions. The third effect below handles external URL changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stack]);

    // ── 3) External URL changes (browser nav, paste, programmatic) ───────
    //
    // If the URL changes and the new signature is NOT one we wrote, treat
    // it as an external mutation: parse the URL and reconcile (open new
    // surface or close the current one). This keeps soft-nav from leaving
    // a stale orchestrator stack.
    useEffect(() => {
        if (!hydratedRef.current) return;

        const sig = overlaySignature(searchParams);
        if (sig === lastWrittenSignatureRef.current) return; // our own write

        const desc = parseOverlayFromUrl(searchParams);
        const top = visibleTop(orchestrator.getStack());

        if (!desc) {
            // URL no longer wants any overlay — close the top if there is one.
            if (top) {
                orchestrator.closeOverlay(top.instanceId, undefined, { immediate: true });
            }
            lastWrittenSignatureRef.current = sig;
            return;
        }

        if (!canRestoreFromUrl(desc.name)) {
            lastWrittenSignatureRef.current = sig;
            return;
        }

        // Open the requested surface if not already top.
        const wantedKey = surfaceKey(desc.surface);
        if (top && surfaceKey(top.surface) === wantedKey) {
            lastWrittenSignatureRef.current = sig;
            return;
        }

        try {
            orchestrator.openOverlay({
                surface: desc.surface,
                overlayKind: 'drawer',
                origin: {
                    origin: { kind: 'page', name: 'deep-link' },
                    context: {
                        surface: { kind: 'page', name: 'deep-link' },
                        pathname: window.location.pathname,
                        search: window.location.search,
                        hash: window.location.hash,
                        capturedAt: Date.now(),
                    },
                    triggerElementId: undefined,
                },
                props: desc.tab ? { tab: desc.tab } : undefined,
            });
            lastWrittenSignatureRef.current = sig;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[overlay-url-sync] external URL open failed:', desc.name, err);
            }
            lastWrittenSignatureRef.current = sig;
        }
    }, [searchParams]);
}
